import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ArticleHero } from '@/components/blog/ArticleHero';
import { TableOfContents } from '@/components/blog/TableOfContents';
import { ArticleContent } from '@/components/blog/ArticleContent';
import { RelatedArticles } from '@/components/blog/RelatedArticles';
import { NewsletterSection } from '@/components/home/NewsletterSection';
import { ArticleSchema } from '@/components/SEO/ArticleSchema';
import { SEOHead } from '@/components/SEO/SEOHead';
import { useArticle } from '@/hooks/useSupabase';
import { useHasPurchased } from '@/hooks/useProfile';
import { Badge } from '@/components/ui/badge';

export function ArticlePage() {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const { t, i18n } = useTranslation('blog');
  const currentLocale = locale || i18n.language || 'fr';
  const { data: article, isLoading, error } = useArticle(slug || '', currentLocale);

  // Fallback FR : vérifier si l'article existe en français quand il n'existe pas dans la langue demandée
  const shouldFallback = !isLoading && !article && currentLocale !== 'fr';
  const { data: frArticle, isLoading: isFrLoading } = useArticle(
    shouldFallback ? (slug || '') : '',
    'fr'
  );

  // Check if user has purchased this article
  const { data: hasPurchased = false } = useHasPurchased(article?.id || '');

  if (isLoading || (shouldFallback && isFrLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-aspexit-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-aspexit-gray">{t('article.loading')}</p>
        </div>
      </div>
    );
  }

  // Rediriger vers la version FR si l'article n'existe pas dans la langue demandée
  if (shouldFallback && frArticle) {
    return <Navigate to={`/fr/blog/${slug}`} replace />;
  }

  if (error || !article) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Article introuvable | Aspexit</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md">
            <h1 className="text-4xl font-heading font-extrabold text-aspexit-black mb-6">
              {t('article.notFound.title')}
            </h1>
            <p className="text-aspexit-gray mb-6">
              {t('article.notFound.message')}
            </p>
            <a
              href={`/${currentLocale}/blog`}
              className="inline-block px-6 py-3 bg-aspexit-green text-white rounded-md font-medium hover:bg-aspexit-green-dark transition-colors"
            >
              {t('article.notFound.backButton')}
            </a>
          </div>
        </div>
      </>
    );
  }

  return (
    <div>
      <SEOHead
        title={article.title}
        description={article.excerpt || article.title}
        path={`/blog/${slug}`}
        image={article.featured_image}
        type="article"
      />
      {/* Schema.org BlogPosting for SEO */}
      <ArticleSchema
        title={article.title}
        excerpt={article.excerpt}
        author="Corentin Leroux"
        publishedAt={article.published_at || article.created_at}
        updatedAt={article.updated_at}
        featuredImage={article.featured_image}
        category={article.category}
        tags={article.tags}
        content={article.content}
        url={`https://aspexit.com/${currentLocale}/blog/${slug}`}
        locale={currentLocale}
        isPremium={article.is_premium}
      />

      {/* Hero Section */}
      <ArticleHero
        category={article.category}
        title={article.title}
        excerpt={article.excerpt}
        author="Corentin Leroux"
        publishedAt={article.published_at || article.created_at}
        readingTime={article.reading_time}
        featuredImage={article.featured_image}
        isPremium={article.is_premium}
      />

      {/* Main Content Area */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
            {/* Article Content */}
            <div className="max-w-3xl">
              <ArticleContent
                content={article.content}
                isPremium={article.is_premium}
                hasPurchased={hasPurchased}
              />

              {/* Tags & Share (Mobile) */}
              <div className="mt-12 pt-8 border-t border-gray-200 lg:hidden">
                {article.tags && article.tags.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-aspexit-gray mb-3">
                      {t('article.tags')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {article.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-aspexit-purple border-aspexit-purple"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar with TOC */}
            <TableOfContents content={article.content} />
          </div>
        </div>
      </section>

      {/* Tags (Desktop) */}
      {article.tags && article.tags.length > 0 && (
        <section className="py-8 bg-aspexit-gray-light hidden lg:block">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <p className="text-sm font-medium text-aspexit-gray mb-3">
                {t('article.tags')}
              </p>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="text-aspexit-purple border-aspexit-purple"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Articles */}
      <RelatedArticles
        currentArticleId={article.id}
        category={article.category}
        tags={article.tags}
      />

      {/* Newsletter CTA */}
      <NewsletterSection />
    </div>
  );
}
